import { orthoTypeToTranslationKeyMap } from '@/src/app/features/ortho/utils/ortho.utils';
import { BaseReportTableComponent } from '@/src/app/features/reports/components/base-report-table/base-report-table.component';
import { PatientLinkComponentModule } from '@/src/app/features/reports/components/patient-link/patient-link.component';
import { ReportFiltersHelperService } from '@/src/app/features/reports/components/report-filters/services/report-filters-helper.service';
import {
  OrthodonticInterventionTypes,
  OrthodonticTreatmentReportRecord,
  OrthoRoutes
} from '@/src/app/features/reports/features/orthodontic-treatments-report/orthodontic-treatments-report.model';
import { GeneralCommonsModule } from '@/src/app/shared/modules/general-commons/general-commons.module';
import { ReadonlyCommonsModule } from '@/src/app/shared/modules/readonly-commons/readonly-commons.module';
import { CommonModule } from '@angular/common';
import { Component, NgModule, OnDestroy, OnInit, inject } from '@angular/core';
import { MatIconModule, MatIcon } from '@angular/material/icon';
import { MatPaginatorModule, MatPaginator } from '@angular/material/paginator';
import { MatSortModule } from '@angular/material/sort';
import {
  MatTableModule,
  MatTable,
  MatColumnDef,
  MatHeaderCellDef,
  MatHeaderCell,
  MatCellDef,
  MatCell,
  MatHeaderRowDef,
  MatHeaderRow,
  MatRowDef,
  MatRow
} from '@angular/material/table';
import { RouterModule, RouterLink } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';

import {
  mixedDentitionAssessmentAppRoute,
  mixedDentitionMidAppRoute,
  mixedDentitionTreatmentAppRoute,
  permanentDentitionAssessmentAppRoute,
  permanentDentitionMidAppRoute,
  permanentDentitionTreatmentAppRoute,
  psioAssessmentAppRoute,
  psioTreatmentAppRoute
} from '@src/app/features/ortho/utils/ortho-routes.consts';
import { SimpleReportCellComponentModule } from '@src/app/features/reports/components/simple-report-cell/simple-report-cell.component';
import { orthoRoute } from '@utils/routing.utils';
import { SimpleReportCellComponent } from '../../../../components/simple-report-cell/simple-report-cell.component';
import { PatientLinkComponent } from '../../../../components/patient-link/patient-link.component';
import { TreatmentStatusIndicatorComponent } from '@shared/components/treatment-status-indicator/treatment-status-indicator.component';
import { TreatmentDateComponent } from '../../../../components/treatment-date/treatment-date.component';
import { ImageThumbnailComponent } from '@shared/components/image-thumbnail/image-thumbnail.component';
import { EmptyTableStateComponent } from '@shared/components/empty-table-state/empty-table-state.component';
import { BracketsPipe } from '@shared/pipes/brackets.pipe';
import { ReportType } from '@src/app/features/reports/components/report-filters/models/report-filters.model';

@Component({
  selector: 'stx-orthodontic-treatment-report-table',
  templateUrl: './orthodontic-treatment-report-table.component.html',
  imports: [
    MatPaginator,
    MatTable,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    SimpleReportCellComponent,
    PatientLinkComponent,
    TreatmentStatusIndicatorComponent,
    RouterLink,
    TreatmentDateComponent,
    MatIcon,
    ImageThumbnailComponent,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    EmptyTableStateComponent,
    TranslateModule,
    BracketsPipe
  ]
})
export class OrthodonticTreatmentReportTableComponent
  extends BaseReportTableComponent<OrthodonticTreatmentReportRecord>
  implements OnInit, OnDestroy
{
  reportFiltersHelperService = inject(ReportFiltersHelperService);
  readonly reportType: ReportType = 'ORTHODONTIC_TREATMENTS';

  orthoRoute = orthoRoute;
  reportColumns: string[] = [
    'orthoType',
    'treatmentCenter',
    'patient',
    'assessment',
    'treatment',
    'midAssessment',
    'applianceType',
    'other'
  ];
  orthoApplianceTypeFieldToTranslationKeyMap: Map<string, string> = new Map<string, string>([
    ['tapingAcrossLip', 'presurgical.tap_lip'],
    ['nasalStent', 'presurgical.nasal_stent'],
    ['maxillaryPlate', 'presurgical.max_appliance'],
    ['namAppliance', 'presurgical.nam'],
    ['fixedApp', 'mixed_dentition.fixed_app'],
    ['maxExp', 'mixed_dentition.max_exp'],
    ['faceMask', 'mixed_dentition.face_mask'],
    ['fullFixedApp', 'permanent_dentition.full_fixed_app'],
    ['splints', 'permanent_dentition.surgical_splints_short']
  ]);

  orthoRoutes: Record<OrthodonticInterventionTypes, OrthoRoutes> = {
    [OrthodonticInterventionTypes.permanentDentition]: {
      treatment: permanentDentitionTreatmentAppRoute,
      assessment: permanentDentitionAssessmentAppRoute,
      midAssessment: permanentDentitionMidAppRoute
    },
    [OrthodonticInterventionTypes.mixedDentition]: {
      treatment: mixedDentitionTreatmentAppRoute,
      assessment: mixedDentitionAssessmentAppRoute,
      midAssessment: mixedDentitionMidAppRoute
    },
    [OrthodonticInterventionTypes.presurgicalInfantOrthopedics]: {
      treatment: psioTreatmentAppRoute,
      assessment: psioAssessmentAppRoute
    }
  };
  orthoTypeToTranslationKeyMap = orthoTypeToTranslationKeyMap;

  constructor() {
    super();
  }

  getOrthoApplianceTypes(recordData: OrthodonticTreatmentReportRecord): string[] {
    const applianceTypes = [];
    this.orthoApplianceTypeFieldToTranslationKeyMap.forEach((translationKey, fieldName) => {
      if (recordData[fieldName]) {
        applianceTypes.push(translationKey);
      }
    });
    return applianceTypes;
  }

  getMidDataToDisplay(recordData: OrthodonticTreatmentReportRecord): string[] {
    const dataToDisplay = [recordData.reimbursableMidEvaluationDate];
    if (recordData.reimbursableMidInScope) {
      dataToDisplay.push(recordData.reimbursableMidOrthodontistName);
    }
    return dataToDisplay;
  }

  ngOnInit(): void {
    super.onInit();

    this.watchTableStructure();
  }

  watchTableStructure() {
    this.subSink.sink = this.reportTableDataService.tableStructure$.subscribe(() => {
      if (this.reportService.reportFiltersFormGroup?.get('includePhotos')?.value && !this.reportColumns.includes('photos')) {
        this.reportColumns.push('photos');
      }

      if (!this.reportService.reportFiltersFormGroup.get('includePhotos')?.value && this.reportColumns.includes('photos')) {
        this.reportColumns = this.reportColumns.filter(columnName => columnName !== 'photos');
      }
    });
  }

  ngOnDestroy(): void {
    super.onDestroy();
  }
}

@NgModule({
  imports: [
    MatTableModule,
    MatSortModule,
    TranslateModule,
    CommonModule,
    MatIconModule,
    RouterModule,
    MatPaginatorModule,
    PatientLinkComponentModule,
    ReadonlyCommonsModule,
    SimpleReportCellComponentModule,
    GeneralCommonsModule,
    OrthodonticTreatmentReportTableComponent
  ],
  exports: [OrthodonticTreatmentReportTableComponent]
})
export class OrthodonticTreatmentReportTableComponentModule {}
